/**
 *	(c) 2024 Приволжский Исследовательский Медицинский Университет
 *
 *	@file: VideoPlayer.jsx
 *  @description: Этот файл является частью клиентской стороны проекта. src Относится к части, которая содержит компоненты - части страниц, допускающие многократное использование в разных частях. VideoPlayer.jsx
 *	@author: Горбас Александр Петрович, Белов Михаил Александрович
 */
import React, { useRef, useState, useEffect } from 'react';

import { fetchProtectedVideo_DataStream } from '../http/Data_API/Protected_Data_API';

import './VideoPlayer.css';

const VideoPlayer = ({ id, idx, category }) => {
    const videoRef = useRef(null);
    const playerContainerRef = useRef(null);
    const progressBarRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [volume, setVolume] = useState(0.7);
    const [isMuted, setIsMuted] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [controlsTimeout, setControlsTimeout] = useState(null);
    const [ videoPath, setVideoPath ] = useState(null);

    // Форматирование времени в MM:SS
    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    // Загрузка метаданных видео
    const handleLoadedMetadata = () => {
        setDuration(videoRef.current.duration);
    };

    // Обновление прогресса
    const handleTimeUpdate = () => {
        if (videoRef.current && videoRef.current.duration) {
            const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
            setProgress(currentProgress);
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    // Переключение воспроизведения
    const togglePlayPause = () => {
        if (isPlaying) {
            videoRef.current.pause();
        } else {
            videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
        showControlsTemporarily();
    };

    // Перемотка по клику на прогресс-бар
    const handleProgressBarClick = (e) => {
        const rect = progressBarRef.current.getBoundingClientRect();
        const pos = (e.pageX - rect.left) / rect.width;
        videoRef.current.currentTime = pos * videoRef.current.duration;
        showControlsTemporarily();
    };

    // Переключение полноэкранного режима
    const toggleFullscreen = async () => {
        try {
            if (!document.fullscreenElement) {
                await playerContainerRef.current.requestFullscreen();
                setIsFullscreen(true);
            } else {
                await document.exitFullscreen();
                setIsFullscreen(false);
            }
        } catch (err) {
            console.error('Ошибка переключения полноэкранного режима:', err);
        }
        showControlsTemporarily();
    };

    // Обработчик изменения состояния полноэкранного режима
    const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
    };

    // Переключение звука
    const toggleMute = () => {
        videoRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
        showControlsTemporarily();
    };

    // Изменение громкости
    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        videoRef.current.volume = newVolume;
        setVolume(newVolume);
        if (newVolume > 0 && isMuted) {
            setIsMuted(false);
        }
        showControlsTemporarily();
    };

    // Показ элементов управления с таймаутом
    const showControlsTemporarily = () => {
        setShowControls(true);
        if (controlsTimeout) clearTimeout(controlsTimeout);
    };

    useEffect(() => {
        // Установка начальной громкости
        if (videoRef.current) {
            videoRef.current.volume = volume;
        }
        
        // Обработчики клавиатуры
        const handleKeyDown = (e) => {
            switch (e.key) {
                case ' ':
                    e.preventDefault();
                    togglePlayPause();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 5);
                    break;
                case 'f':
                    toggleFullscreen();
                    break;
                case 'm':
                    toggleMute();
                    break;
                case 'Escape':
                    if (isFullscreen) {
                        toggleFullscreen();
                    }
                    break;
                default:
                    break;
            }
        };

        // Добавление обработчиков событий
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            // Удаление обработчиков событий при размонтировании
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('keydown', handleKeyDown);
            if (controlsTimeout) clearTimeout(controlsTimeout);
        };
    }, [isPlaying, isFullscreen, isMuted, volume]);

    useEffect( () => {
        fetchProtectedVideo_DataStream({ id, idx, category }).then( s => s?.error ? null : setVideoPath(s) );
    }, [ id, idx, category ]);

    if (!videoPath)
        return;

    return (
        <div className={`video-player-container ${isFullscreen ? 'fullscreen' : ''}`} ref={playerContainerRef}>
            <div 
                className="video-wrapper"
                onMouseEnter={showControlsTemporarily}
                onMouseMove={showControlsTemporarily}
                onMouseLeave={() => {
                if (!isFullscreen) setShowControls(false);
                }}
            >
                <video
                    ref={videoRef}
                    onClick={togglePlayPause}
                    onDoubleClick={toggleFullscreen}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                    preload='metadata'
                    loading='lazy'
                    playsInline
                >
                    <source src={videoPath} type='video/mp4' />
                </video>

                <div className={`video-controls ${showControls ? 'visible' : 'hidden'}`}>
                    <div 
                        className="progress-bar-container" 
                        ref={progressBarRef}
                        onClick={handleProgressBarClick}
                    >
                        <div 
                            className="progress-bar" 
                            style={{ width: `${progress}%` }}
                        ></div>
                        <div 
                            className="progress-bar-buffer" 
                            style={{ width: `${videoRef.current?.buffered.length ? (videoRef.current.buffered.end(0) / videoRef.current.duration) * 100 : 0}%` }}
                        ></div>
                    </div>

                    <div className="bottom-controls">
                        <div className="left-controls">
                            <button className="control-button" onClick={togglePlayPause}>
                                {isPlaying ? (
                                    <img src='/videoplayer_icons/pause.svg' alt="Pause" />
                                ) : (
                                    <img src='/videoplayer_icons/play.svg' alt="Play" />
                                )}
                            </button>
                            
                            <div className="time-display">
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </div>
                        </div>
                        
                        <div className="right-controls">
                                                <div className="volume-control">
                            <button className="control-button" onClick={toggleMute}>
                                {isMuted || volume === 0 ? (
                                    <img src='/videoplayer_icons/mute.svg' alt="Mute" />
                                ) : (
                                    <img src='/videoplayer_icons/volume.svg' alt="Volume" />
                                )}
                            </button>
                            <div className="volume-slider-container">
                                <div className="volume-slider-track">
                                    <div 
                                        className="volume-slider-fill" 
                                        style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
                                    />
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={isMuted ? 0 : volume}
                                    onChange={handleVolumeChange}
                                    className="volume-slider"
                                />
                            </div>
                        </div>
                            
                            <button className="control-button" onClick={toggleFullscreen}>
                                {isFullscreen ? (
                                    <img src='/videoplayer_icons/collapse.svg' alt="Exit fullscreen" />
                                ) : (
                                    <img src='/videoplayer_icons/extend.svg' alt="Fullscreen" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {!isPlaying && (
                    <div className="play-overlay visible" onClick={togglePlayPause}>
                        <img src='/videoplayer_icons/play.svg' alt="Play" />
                    </div>
                )}
            </div>
        </div>
     );
 };
 
 export default VideoPlayer;